import { useCallback } from 'react';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	useFetchWithCache,
	type useFetchWithCacheReturn,
} from '../common/utils/useFetchWithCache.tsx';
import type { ProjectAccessLevel, ProjectAccessLevels, UserRoles } from '../common/types.tsx';

const getProjectAccessLevels = async (): Promise<ProjectAccessLevels> => {
	return performGetRequest('/rest/internal/simplified/1.0/accesslevel?projectType=software');
};
export const getUserRoles = (projectKey?: string): Promise<UserRoles> => {
	if (!projectKey) {
		throw new Error('Project Key is required to fetch user roles');
	}

	return performGetRequest(
		`/rest/api/3/project/${projectKey}/roledetails?excludeConnectAddons=true`,
	);
};

const getProjectAccessLevel = async (projectId?: number): Promise<ProjectAccessLevel> => {
	if (!projectId) {
		throw new Error('Project ID is missing while fetching project access level.');
	}

	return performGetRequest(`/rest/internal/simplified/1.0/accesslevel/project/${projectId}`);
};

type useFetchExtendedDialogDataHookReturn = useFetchWithCacheReturn<
	[ProjectAccessLevels, UserRoles, ProjectAccessLevel]
>;

export type ExtendedDialogData = useFetchExtendedDialogDataHookReturn['status'];

export const useFetchExtendedDialogData = (): useFetchExtendedDialogDataHookReturn => {
	const { data: projectContext } = useProjectContext();
	const callback = useCallback(
		() =>
			Promise.all([
				getProjectAccessLevels(),
				getUserRoles(projectContext?.projectKey),
				getProjectAccessLevel(projectContext?.projectId),
			]),
		[projectContext?.projectId, projectContext?.projectKey],
	);

	return useFetchWithCache(callback, 'fetchExtendedDialogData');
};
